<template>
    <div class="orgstructure-content">
        <div class="orgstructure-tree">
            <ai-tree-list-edit-core
                ref="treeListRef"
                :meta="meta"
                :dataSource="displayList"
                :args="args"
                :toolbar-user-items="toolbarUserItems"
                :to-remove-toolbar-items-names="toRemoveToolbarItemsNames"
                :editing="editing"
                @toolbar-button-click="toolbarButtonClick"
            >
            </ai-tree-list-edit-core>
        </div>
        <div class="orgstructure-card">
            <component :is="componentCardName" ref="cardRef" @NewItemSaved="onExistingItemSaved" />
        </div>
        <dx-popup
            ref="popupRef"
            :drag-enabled="false"
            height="95%"
            :hide-on-outside-click="true"
            :show-close-button="true"
            :show-title="true"
            :title="popupTitle"
            :visible="popupVisible"
            @hidden="changePopupVisibility(false)"
        >
            <div class="orgstructure-card">
                <component :is="componentNewCardName" ref="newCardRef" @NewItemSaved="onNewItemSaved" />
            </div>
        </dx-popup>
    </div>
</template>

<script>
export default {
    name: 'org-structure-view',
};
</script>

<script setup>
import { /*computed,*/ ref, onMounted, reactive, computed /*, nextTick*/ } from 'vue';
import aiTreeListEditCore from '@/components/ai-tree-list-edit/ai-tree-list-edit-core.vue';
import { deleteNsiOrgData, deleteNsiEmpData, moveOrgEmp } from '@/service/nsi-service';
import { getDataSource } from '@/service/meta-service';
import { DxPopup } from 'devextreme-vue';
import { confirm } from 'devextreme/ui/dialog';
import { dxUiNotify } from '@/utils/dx-helper';

const componentCardName = ref(null);
const cardRef = ref();
const componentNewCardName = ref(null);
const newCardRef = ref();
const emit = defineEmits(['toolbarButtonClick']);
const prHist = ref(false);
const prEs = ref(true);
const prHistValue = computed(() => {
    return prHist.value;
});

const prEsValue = computed(() => {
    return prEs.value;
});

const state = reactive({
    dataSource: [],
    prefState: true,
    selectedRowKeys: [],
    treeListChanges: [],
    userSettings: {},
});

const displayList = computed(() => state.dataSource);

const editing = computed(() => {
    return {
        enableDeleting: true,
        allowAdding: true,
        allowUpdating: true,
    };
});

onMounted(async () => {
    treeListInstance.value = treeListRef.value.treeListInstance;
    treeListInstance.value.on('focusedRowChanged', onItemClick);
    treeListInstance.value.on('rowPrepared', onRowPrepared);
    treeListInstance.value.option('showRowLines', false);
    await setDataSource(meta.api.select, args.select);
});

window.onblur = function () {
    if (treeListRef.value) treeListInstance.value?.option('focusedElement', '');
};

const treeListRef = ref();
const treeListInstance = ref();

let itemToMove = { type: null, id: null, name: null };

const itemToMoveInitialize = () => {
    itemToMove = { type: null, id: null, name: null };
};

const setDataSource = async (url, args) => {
    const { requiredArgs } = meta;

    if (requiredArgs) {
        const hasAllRequiredArgs = requiredArgs.select.every(arg => Object.prototype.hasOwnProperty.call(args, arg));

        if (!hasAllRequiredArgs) {
            state.dataSource = [];
            return;
        }
    }

    const { result, data } = await getDataSource(url, args);
    state.dataSource = result ? data : [];
};

const onItemClick = event => {
    if (event.row) {
        if (event.row.data.e === 'KR_ORG') {
            componentCardName.value = null;
            setTimeout(async () => {
                componentCardName.value = 'nsi-org-card';
            }, 300);
            setTimeout(async () => {
                cardRef.value?.SetId(event.row.data.id, event.row.data.parent_id);
                cardRef.value?.forceRefresh();
            }, 400);
        } else if (event.row.data.e === 'KR_EMPLOYEE') {
            componentCardName.value = null;
            setTimeout(async () => {
                componentCardName.value = 'nsi-emp-card';
            }, 300);
            setTimeout(async () => {
                cardRef.value?.SetId(event.row.data.id, event.row.data.parent_id);
                cardRef.value?.forceRefresh();
            }, 400);
        }
    }
};

const onRowPrepared = event => {
    if (event.data?.pr_active === 1) event.rowElement.style.textDecoration = 'line-through';
};

const onRefreshClick = async () => {
    await setDataSource(meta.api.select, args.select);
};

const onAddRootRowClick = async () => {
    const key = treeListInstance.value.option('focusedRowKey');
    if (key === null) return;
    const focusedRowData = await treeListInstance.value.getDataByKeys([key]);
    const this_node_type = focusedRowData[0]['e'];
    if (this_node_type === 'KR_EMPLOYEE') return;
    const parent_key = focusedRowData[0]['parent_id'];
    if (!parent_key) {
        dxUiNotify('Добавление организации на верхнем уровне иерархии невозможно.', 'error');
        return;
    }
    changePopupVisibility(true);
    componentNewCardName.value = 'nsi-org-card';
    setTimeout(async () => {
        newCardRef.value?.SetId(0, parent_key);
        newCardRef.value?.forceRefresh();
    }, 300);
};

const onAddChildRowClick = async () => {
    const key = treeListInstance.value.option('focusedRowKey');
    if (!key) return;
    const focusedRowData = await treeListInstance.value.getDataByKeys([key]);
    const this_node_type = focusedRowData[0]['e'];
    if (this_node_type === 'KR_EMPLOYEE') return;
    const parent_key = focusedRowData[0]['id'];
    changePopupVisibility(true);
    componentNewCardName.value = 'nsi-org-card';
    setTimeout(async () => {
        newCardRef.value?.SetId(0, parent_key);
        newCardRef.value?.forceRefresh();
    }, 300);
};

const onAddEmployeeButton = async () => {
    const key = treeListInstance.value.option('focusedRowKey');
    if (!key) return;
    const focusedRowData = await treeListInstance.value.getDataByKeys([key]);
    const this_node_type = focusedRowData[0]['e'];
    const org_key = this_node_type === 'KR_ORG' ? focusedRowData[0]['id'] : focusedRowData[0]['parent_id'];
    changePopupVisibility(true);
    componentNewCardName.value = 'nsi-emp-card';
    setTimeout(async () => {
        newCardRef.value?.SetId(0, org_key);
        newCardRef.value?.forceRefresh();
    }, 300);
};

const onDeleteButton = async () => {
    const key = treeListInstance.value.option('focusedRowKey');
    if (!key) return;
    const focusedRowData = await treeListInstance.value.getDataByKeys([key]);
    if (focusedRowData[0]['e'] === 'KR_ORG') {
        const depName = focusedRowData[0]['name'];
        await confirm(`Вы уверены, что хотите удалить подразделение "${depName}"?`, 'Подтвердите удаление').then(
            res => {
                if (res) {
                    deleteNsiOrgData(focusedRowData[0]['id']).then(result => {
                        if (result.success === true) {
                            dxUiNotify(`Подразделение "${depName}" удалено.`, 'success');
                            setDataSource(meta.api.select, args.select);
                        } else {
                            dxUiNotify(result.errors.message, 'error');
                        }
                    });
                }
            }
        );
    } else if (focusedRowData[0]['e'] === 'KR_EMPLOYEE') {
        const FIO = focusedRowData[0]['name'];
        await confirm(`Вы уверены, что хотите удалить сотрудника "${FIO}"?`, 'Подтвердите удаление').then(res => {
            if (res) {
                deleteNsiEmpData(focusedRowData[0]['id']).then(result => {
                    if (result.success === true) {
                        dxUiNotify(`Сотрудник "${FIO}" удален.`, 'success');
                        setDataSource(meta.api.select, args.select);
                    } else {
                        dxUiNotify(result.errors.message, 'error');
                    }
                });
            }
        });
    }
};

const applyFilterESButton = () => {
    prEs.value = !prEs.value;
    onRefreshClick();
};

const applyFilterHistButton = () => {
    prHist.value = !prHist.value;
    onRefreshClick();
};

const moveToButton = async () => {
    const key = treeListInstance.value.option('focusedRowKey');
    if (!key) return;
    const focusedRowData = await treeListInstance.value.getDataByKeys([key]);
    itemToMove = { type: focusedRowData[0]['e'], id: focusedRowData[0]['id'], name: focusedRowData[0]['name'] };
};

const moveSaveButton = async () => {
    if (itemToMove.type === null && itemToMove.id === null) {
        dxUiNotify('Необходимо выбрать элемент для переноса', 'error');
        return;
    }
    const key = treeListInstance.value.option('focusedRowKey');
    if (key === null) return;
    const focusedRowData = await treeListInstance.value.getDataByKeys([key]);
    if (focusedRowData[0]['e'] === 'KR_EMPLOYEE') {
        dxUiNotify('Необходимо выбрать подразделение', 'error');
        itemToMoveInitialize();
        return;
    }

    await confirm(
        `Вы уверены, что хотите перенести ${itemToMove.type === 'KR_ORG' ? 'подразделение' : 'сотрудника'} "${
            itemToMove.name
        }" в подразделение "${focusedRowData[0]['name']}"?`,
        'Подтвердите перенос'
    ).then(res => {
        if (res) {
            moveOrgEmp({ src_type: itemToMove.type, src_id: itemToMove.id, trg_id: focusedRowData[0]['id'] }).then(
                result => {
                    if (result.success) {
                        dxUiNotify('Перенос выполнен', 'success');
                        setDataSource(meta.api.select, args.select).then(() => {
                            let moved_item_key = (itemToMove.type === 'KR_ORG' ? 'org_' : 'emp_') + itemToMove.src_id;
                            treeListInstance.value.option('focusedRowKey', moved_item_key);
                            treeListInstance.value.option('selectedRowKeys', [moved_item_key]);
                        });
                    } else {
                        dxUiNotify(result.errors[0].message, 'error');
                    }
                    itemToMoveInitialize();
                }
            );
        }
    });
};

const onNewItemSaved = async id => {
    changePopupVisibility(false);
    await setDataSource(meta.api.select, args.select);
    treeListInstance.value.option('focusedRowKey', id);
    treeListInstance.value.option('selectedRowKeys', [id]);
    dxUiNotify('Данные сохранены', 'success');
};

const onExistingItemSaved = async id => {
    await setDataSource(meta.api.select, args.select);
    treeListInstance.value.option('focusedRowKey', id);
    dxUiNotify('Данные сохранены', 'success');
};

const toolbarButtonClick = async e => {
    switch (e.item.component.name) {
        case 'refresh':
            await onRefreshClick(e);
            break;
        case 'addDepButton':
            await onAddRootRowClick(e);
            break;
        case 'addChildDepButton':
            await onAddChildRowClick(e);
            break;
        case 'addEmployeeButton':
            await onAddEmployeeButton(e);
            break;
        case 'deleteDepEmpButton':
            await onDeleteButton(e);
            break;
        case 'filterESButton':
            await applyFilterESButton(e);
            break;
        case 'filterHistButton':
            await applyFilterHistButton(e);
            break;
        case 'moveTo':
            moveToButton(e);
            break;
        case 'moveSave':
            moveSaveButton(e);
            break;

        default:
            break;
    }
    emit('toolbarButtonClick', e);
};

const popupTitle = ref();

const popupVisible = ref(false);

const changePopupVisibility = value => {
    if (!value) {
        componentNewCardName.value = null;
    }
    popupVisible.value = value;
};

const meta = {
    searchHint: null,
    keyExpr: 'id_t',
    displayExpr: 'name',
    parentIdExpr: 'parent_id_t',
    dataStructure: 'plain',
    rootValue: null,
    selectByClick: true,
    selectionMode: 'single',
    expandEvent: 'dbclick',
    itemTemplate: 'item',
    valueChangeEvent: 'keyup',
    animationEnabled: false,
    scrollingMode: 'standard',
    expandAll: true,
    firstColumnFields: {
        text: 'name',
        svg: 'img_src',
        caption: 'Наименование',
        allowEditing: false,
    },
    columns: [
        {
            dataField: 'id',
            allowEditing: false,
            visible: false,
        },
    ],
    api: {
        select: 'admin./department/official/tree/get',
    },
};
const args = reactive({
    select: {
        meta_args: {
            select: 'payer.get_orgstructure_tree',
        },
        select_args: {
            pr_hist_: prHistValue,
            pr_es_: prEsValue,
        },
    },
});

const toRemoveToolbarItemsNames = [
    'choice',
    'edit',
    'expandOrCollapseButton',
    'copyButton',
    'addRootRowButton',
    'addChildRowButton',
    'addSibling',
    'addChild',
    'delete',
    'save',
    'copy',
    'show-column-chooser',
];

const toolbarUserItems = [
    {
        widget: 'dx-drop-down-button',
        index: 0,
        hint: 'Фильтры',
        component: {
            name: 'filters',
            htmlElement: `<svg width="48" height="48" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="2.5" y="4" width="7.5" height="1" rx="0.5" fill="#286EB6"/>
<rect x="4" y="6" width="4.5" height="1" rx="0.5" fill="#286EB6"/>
<rect x="5" y="8" width="2.5" height="1" rx="0.5" fill="#286EB6"/>
</svg>`,
            items: [
                reactive({
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'filterESButton',
                        text: 'Показывать только энергосбыт',
                        isChecked: prEsValue,
                        htmlElement: 'ai-checkbox',
                    },
                }),
                reactive({
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'filterHistButton',
                        text: 'Показывать архивные',
                        isChecked: prHistValue,
                        htmlElement: 'ai-checkbox',
                    },
                }),
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-button',
        index: 6,
        component: {
            name: 'addDepButton',
            hint: 'Добавить подразделение',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-add-sibling-base',
        },
    },
    {
        widget: 'dx-button',
        index: 7,
        component: {
            name: 'addChildDepButton',
            hint: 'Добавить дочернее подразделение',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-add-child-base',
        },
    },
    {
        widget: 'dx-button',
        index: 8,
        component: {
            name: 'addEmployeeButton',
            hint: 'Добавить специалиста',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-emp-add',
        },
    },
    {
        widget: 'dx-button',
        index: 9,
        component: {
            name: 'deleteDepEmpButton',
            disabledRefName: 'isDeleteButtonDisabled',
            hint: 'Удалить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-delete-base',
        },
    },
];
</script>
<style>
.orgstructure-content {
    display: flex;
    flex-direction: row;
    height: 100%;
    width: 100%;
    padding: 5px 5px 0 5px;
}

.orgstructure-tree {
    display: flex;
    height: 100%;
    flex-basis: 45%;
}

.orgstructure-card {
    display: flex;
    height: 100%;
    flex-basis: 55%;
}
</style>
